import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, qrUrl } from "@/lib/api";
import { useLocale } from "@/i18n/useLocale";
import { useAuth } from "@/context/AuthContext";
import { TemplateRenderer } from "@/components/templates/TemplateRenderer";
import { IndustryCard } from "@/components/landing/IndustryCard";
import { INDUSTRY_CARDS } from "@/lib/industryCards";
import { industryById } from "@/lib/industries";
import IndustryCustomizer from "@/components/admin/IndustryCustomizer";
import CardInfoTabs, { emptyCard } from "@/components/admin/CardInfoTabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Loader2, Check, Copy, ExternalLink, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const AriadniMark = ({ className = "" }) => (
  <img src="/tp-mark.png" alt="TapPresence" className={`object-contain ${className}`} />
);

const STEPS = [
  { n: 1, key: "step_industry" },
  { n: 2, key: "step_style" },
  { n: 3, key: "step_info" },
];

export default function CreateCard() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const { user, workspace } = useAuth() || {};
  const [form, setForm] = useState(() => ({
    ...emptyCard,
    identity: {
      ...emptyCard.identity,
      fullName: user?.name || "",
      email: user?.email || "",              // reserved for future identity field
      country: user?.country || "",
      company: workspace?.name || "",
    },
    contact: {
      ...emptyCard.contact,
      email: user?.email || "",
    },
  }));
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState(null);

  // Reset scroll to top whenever the user changes step. Fixes the reported
  // "starts mid-page after Continue" behaviour.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const set = (path, value) => {
    setForm((f) => {
      const next = { ...f };
      if (path.includes(".")) { const [g, k] = path.split("."); next[g] = { ...next[g], [k]: value }; }
      else next[path] = value;
      return next;
    });
  };

  const pickIndustry = (id) => {
    const card = INDUSTRY_CARDS.find((c) => c.id === id);
    const ind = industryById(id);
    setForm((f) => ({
      ...f,
      industry: id,
      accent: card?.accentId || "gold",
      custom_accent_color: "",
      background_style: ind?.styles?.[0]?.id || "",
      background_opacity: ind?.defaultOpacity ?? 0.14,
    }));
    setStep(2);
  };

  const save = async (publish) => {
    const name = form.identity.fullName?.trim();
    if (!name) { toast.error(t("createCard.addNameFirst")); setStep(3); return; }
    const slug = (form.slug.trim() || name).toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-");
    setSaving(true);
    try {
      await api.post("/admin/cards", { ...form, slug, status: publish ? "published" : "draft" });
      if (publish) {
        setPublished({ slug, name });
      } else {
        toast.success(t("createCard.draftSaved"));
        navigate("/admin");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("createCard.couldNotSaveCard"));
    } finally { setSaving(false); }
  };

  const industryChosen = !!form.industry;

  if (published) {
    const link = `${window.location.origin}/${published.slug}`;
    return (
      <div className="aria-dark relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050607] px-6 text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="publish-success">
        <div className="grain-overlay" style={{ opacity: 0.05 }} />
        <div className="aria-gold-radial pointer-events-none absolute inset-0" />
        <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur-xl shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
          <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "rgba(214,166,83,0.14)", boxShadow: "0 0 30px rgba(214,166,83,0.3)" }}>
            <PartyPopper className="h-7 w-7 text-[#D6A653]" />
          </span>
          <h1 className="text-3xl font-light tracking-tight">{t("createCard.youreLive")}</h1>
          <p className="mt-2 text-sm text-white/55">{published.name ? t("createCard.liveSubNamed", { name: published.name }) : t("createCard.liveSubYours")}</p>
          <div className="mx-auto mt-6 w-fit rounded-2xl border border-white/10 bg-white p-3">
            <img src={qrUrl(published.slug)} alt="QR code" className="h-40 w-40 rounded" data-testid="publish-qr" />
          </div>
          <div className="mt-5 flex items-center gap-2 rounded-full border border-white/12 bg-black/30 px-4 py-2.5 text-sm">
            <span className="truncate text-white/70" data-testid="publish-link">{link}</span>
            <button onClick={() => { navigator.clipboard.writeText(link); toast.success(t("createCard.linkCopied")); }} className="ml-auto shrink-0 text-[#D6A653] hover:text-[#E8B764]" data-testid="publish-copy"><Copy className="h-4 w-4" /></button>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <a href={`/${published.slug}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-full border border-white/15 py-3 text-sm text-white transition-colors hover:bg-white/5" data-testid="publish-view">
              {t("createCard.viewCard")} <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button onClick={() => navigate("/admin")} className="rounded-full bg-[#D6A653] py-3 text-sm font-medium text-[#050607] transition-all hover:bg-[#E8B764]" data-testid="publish-dashboard">
              {t("createCard.goDashboard")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aria-dark relative min-h-screen overflow-x-hidden bg-[#050607] text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="create-card-studio">
      <div className="grain-overlay" style={{ opacity: 0.04 }} />

      {/* header */}
      <header className="sticky top-0 z-40 border-b border-white/8 bg-[#050607]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-4 py-4 sm:px-8">
          <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-white" data-testid="create-back">
            <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">{t("createCard.back")}</span>
          </button>
          <div className="hidden items-center gap-2.5 md:flex">
            <AriadniMark className="h-5 w-5 text-[#D6A653]" />
            <span className="text-[15px] font-medium">{t("createCard.headerTitle")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => save(false)} disabled={saving || !industryChosen} className="rounded-full border border-white/15 bg-transparent px-3 text-white hover:bg-white/5 sm:px-4" data-testid="save-draft-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("createCard.saveDraft")}
            </Button>
            <Button onClick={() => save(true)} disabled={saving || !industryChosen} className="rounded-full bg-[#D6A653] px-3 font-medium text-[#050607] transition-all hover:bg-[#E8B764] hover:shadow-[0_0_18px_rgba(214,166,83,0.35)] active:scale-95 sm:px-4" data-testid="publish-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("createCard.publish")}
            </Button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto w-full max-w-7xl min-w-0 px-4 pb-20 pt-8 sm:px-8">
        {/* stepper */}
        <div className="mb-8 flex flex-wrap items-center gap-2">
          {STEPS.map((s, i) => {
            const active = step === s.n;
            const done = step > s.n;
            const disabled = s.n > 1 && !industryChosen;
            return (
              <button key={s.n} onClick={() => !disabled && setStep(s.n)} disabled={disabled}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all ${active ? "border-[#D6A653] bg-[#D6A653]/12 text-white" : done ? "border-[#D6A653]/30 text-[#D6A653]" : "border-white/12 text-white/45"} ${disabled ? "cursor-not-allowed opacity-40" : "hover:border-white/30"}`}
                data-testid={`step-${s.n}`}>
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${active || done ? "bg-[#D6A653] text-[#050607]" : "bg-white/10 text-white/60"}`}>{done ? <Check className="h-3 w-3" /> : s.n}</span>
                {t(`createCard.${s.key}`)}
                {i < STEPS.length - 1 && <ArrowRight className="ml-1 hidden h-3.5 w-3.5 text-white/25 sm:block" />}
              </button>
            );
          })}
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
          {/* step content — min-w-0 so long fields / tab labels can't push
              the grid track wider than the viewport on mobile Safari. */}
          <div className="min-w-0 max-w-full">
            {step === 1 && (
              <div>
                <h1 className="text-3xl font-light tracking-tight text-white">{t("createCard.step1Title")}</h1>
                <p className="mt-2 text-white/55">{t("createCard.step1Sub")}</p>
                <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D6A653]">{t("createCard.chooseIndustry")}</p>
                {/* Outer wrapper clips at the column edge so the -mx-4 bleed on the
                    inner flex can no longer push the whole page horizontally on
                    mobile Safari. The inner flex retains overflow-x-auto for the
                    actual swipe / snap behaviour. */}
                <div className="mt-5 w-full min-w-0 max-w-full overflow-hidden">
                  <div className="-mx-4 flex snap-x snap-mandatory gap-6 overflow-x-auto px-4 pb-2 min-w-0" data-testid="industry-scroller">
                    {INDUSTRY_CARDS.map((c) => (
                      <button key={c.id} onClick={() => pickIndustry(c.id)} className="group snap-start shrink-0 w-[280px] text-left focus:outline-none" data-testid={`choose-industry-${c.id}`}>
                        <div className={`rounded-[30px] transition-all ${form.industry === c.id ? "ring-2 ring-[#D6A653] ring-offset-4 ring-offset-[#050607]" : "opacity-90 group-hover:opacity-100 group-hover:-translate-y-1"}`}>
                          <IndustryCard c={c} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D6A653]">{t("createCard.step2Eyebrow")}</p>
                <h2 className="mt-2 text-2xl font-light tracking-tight text-white">{t("createCard.step2Title")}</h2>
                <p className="mt-1 text-sm text-white/50">{t("createCard.step2Sub")}</p>
                <div className="mt-6">
                  <IndustryCustomizer form={form} set={set} hideIndustry={true} />
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <Button onClick={() => setStep(1)} className="rounded-full border border-white/15 bg-transparent text-white hover:bg-white/5"><ArrowLeft className="mr-1 h-4 w-4" /> {t("createCard.industryBtn")}</Button>
                  <Button onClick={() => setStep(3)} className="rounded-full bg-[#D6A653] font-medium text-[#050607] hover:bg-[#E8B764]" data-testid="to-information">{t("createCard.continue")} <ArrowRight className="ml-1 h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D6A653]">{t("createCard.step3Eyebrow")}</p>
                <h2 className="mt-2 text-2xl font-light tracking-tight text-white">{t("createCard.step3Title")}</h2>
                <div className="mt-5 mb-4 max-w-xs">
                  <Label className="text-xs text-white/55">{t("createCard.cardLink")}</Label>
                  <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder={t("createCard.slugPlaceholder")} data-testid="editor-slug" />
                </div>
                <CardInfoTabs form={form} setForm={setForm} showIndustry={false} onPublish={() => save(true)} publishing={saving} defaultCountryIso={form.identity.country} />
                <div className="mt-6 flex items-center justify-between">
                  <Button onClick={() => setStep(2)} className="rounded-full border border-white/15 bg-transparent text-white hover:bg-white/5"><ArrowLeft className="mr-1 h-4 w-4" /> {t("createCard.styleBtn")}</Button>
                  <Button onClick={() => save(true)} disabled={saving} className="rounded-full bg-[#D6A653] font-medium text-[#050607] hover:bg-[#E8B764]" data-testid="publish-button-bottom">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("createCard.previewPublish")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* persistent live preview */}
          <div className="h-fit lg:sticky lg:top-24">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#D6A653]">{t("createCard.livePreview")}</p>
            <div className="mx-auto w-full max-w-[360px] overflow-hidden rounded-[2.2rem] border-[6px] border-[#141518] bg-[#050607] shadow-[0_30px_80px_rgba(0,0,0,0.6)] ring-1 ring-white/10">
              <div className="overflow-y-auto" style={{ height: "72vh" }} data-testid="create-preview">
                <TemplateRenderer data={form} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
